import React, { useCallback, useState } from "react";
import styled from "styled-components";
import { ZIndexes, content } from "@glossy";
import { EventOnChangeCanvasCursor } from "@linkfluence/models/events/onChangeCanvasCursor";
import { Locale } from "@linkfluence/models/locale";
import Icon from "@ui-kit/icon";
import { useFeature } from "@radarly/core/features/use-cases";
import { tr } from "@radarly/i18n";

const isCursorMagicWand = (element: HTMLElement) => {
  const cursorStyle = getComputedStyle(element).cursor || ""; // Style after hovering, eg `url("http://....svg")`
  return cursorStyle.includes(".svg"); // Check if it's a custom cursor image, ie the magic wand asset url
};

const MagicWandIcon = styled(Icon)`
  flex-shrink: 0;
`;

const MagicFooterWrapper = styled.div`
  position: absolute;
  z-index: ${ZIndexes.Tooltips};
  right: 4px;
  bottom: 4px;
  padding: 4px 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  color: ${content};
`;

export const MagicFooter = ({ locale }: { locale: Locale }) => (
  <MagicFooterWrapper>
    <MagicWandIcon glyph="magic-wand-gradient" />
    {tr(locale, "CUSTOM_PAGES.WIDGET.CONTEXTUAL_EXPLAINER_HOVER_TEXT")}
  </MagicFooterWrapper>
);

/**
 * Show/Hide the "feature explanation" text according to whether icon is a magic-wand or not within the Tile
 * To track a cursor icon change, we listen to event `onMouseOver` and custom event `onChangeCanvasCursor` (for canvas charts)
 */
export const useExplainer = (locale: Locale) => {
  const isContextualExplainerEnabled = useFeature("contextual_explainer");
  const [showExplainer, setShowExplainer] = useState(false);

  // On mouse over check if the cursor has changed into a magic wand
  const handleMouseOver = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (e.target instanceof HTMLElement) {
      setShowExplainer(isCursorMagicWand(e.target));
    }
  };

  // Elements inside a canvas don't trigger `onMouseOver`
  // To circumvent this we manually trigger a custom event `onChangeCanvasCursor` on hover
  const handleChangeCanvasCursor = (e: EventOnChangeCanvasCursor) => {
    if (e.target instanceof HTMLElement) {
      setShowExplainer(isCursorMagicWand(e.target));
    }
  };

  const explainerRef = useCallback((node: HTMLDivElement) => {
    node?.addEventListener("onChangeCanvasCursor", handleChangeCanvasCursor);
    return () => {
      node?.removeEventListener(
        "onChangeCanvasCursor",
        handleChangeCanvasCursor
      );
    };
  }, []);

  return {
    magicFooter:
      isContextualExplainerEnabled && showExplainer ? (
        <MagicFooter locale={locale} />
      ) : null,
    handleMouseOver,
    explainerRef,
  };
};
